import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { UserService } from './user.service';
import { ProfileService } from './profile.service';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class UserPermissionsService {
  private userPermissionsSubject = new BehaviorSubject<string[]>([]);
  public userPermissions$: Observable<string[]> = this.userPermissionsSubject.asObservable();

  private permissionsLoaded = false;
  private loadingPromise: Promise<void> | null = null; // Promise compartilhada para evitar múltiplas chamadas simultâneas

  constructor(
    private userService: UserService,
    private profileService: ProfileService
  ) {
    // Não carregar permissões no construtor para evitar problemas de inicialização
    // As permissões serão carregadas quando necessário (lazy loading)
  }

  /**
   * Carrega as permissões do usuário atual
   * Usa uma promise compartilhada para evitar múltiplas chamadas simultâneas ao /me
   */
  async loadUserPermissions(): Promise<void> {
    // Se já está carregado, retornar imediatamente
    if (this.permissionsLoaded) {
      return;
    }

    // Se já existe uma requisição em andamento, aguardar ela
    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    // Criar promise compartilhada para evitar múltiplas chamadas simultâneas
    this.loadingPromise = (async () => {
      // Verificar se há token antes de tentar carregar
      if (typeof window === 'undefined' || !window.localStorage) {
        this.userPermissionsSubject.next([]);
        this.permissionsLoaded = true;
        this.loadingPromise = null;
        return;
      }

      const token = localStorage.getItem('jwt') || localStorage.getItem('jwt-password');
      if (!token) {
        this.userPermissionsSubject.next([]);
        this.permissionsLoaded = true;
        this.loadingPromise = null;
        return;
      }

      try {
        const user = await this.userService.getMe();
        
        if (!user) {
          this.userPermissionsSubject.next([]);
          this.permissionsLoaded = true;
          this.loadingPromise = null;
          return;
        }

        // Se o usuário já tem permissionKeys diretamente
        if (user.permissionKeys && Array.isArray(user.permissionKeys)) {
          this.userPermissionsSubject.next(user.permissionKeys);
          this.permissionsLoaded = true;
          this.loadingPromise = null;
          return;
        }

        // Se o usuário tem profileIds ou profilesIds, buscar as permissões dos perfis
        // A API pode retornar 'profilesIds' (com 's') ou 'profileIds' (sem 's')
        const profileIdsArray = (user.profilesIds || user.profileIds) as string[] | undefined;
        if (profileIdsArray && Array.isArray(profileIdsArray) && profileIdsArray.length > 0) {
          const allPermissions = new Set<string>();
          const profileIds: string[] = profileIdsArray;
          
          if (!environment.production) {
            console.debug(`Buscando permissões de ${profileIds.length} perfil(is):`, profileIds);
          }
          
          // Buscar permissões de cada perfil usando Promise
          const profilePromises = profileIds.map((profileId: string) => {
            return new Promise<void>((resolve) => {
              this.profileService.getById(profileId).subscribe({
                next: (profile) => {
                  if (profile && profile.permissionKeys) {
                    if (!environment.production) {
                      console.debug(`Perfil ${profileId} encontrado com ${profile.permissionKeys.length} permissão(ões):`, profile.permissionKeys);
                    }
                    profile.permissionKeys.forEach(key => allPermissions.add(key));
                  } else {
                    if (!environment.production) {
                      console.warn(`Perfil ${profileId} não possui permissionKeys`);
                    }
                  }
                  resolve();
                },
                error: (error) => {
                  console.warn(`Erro ao buscar perfil ${profileId}:`, error);
                  resolve(); // Continuar mesmo com erro
                }
              });
            });
          });
          
          // Aguardar todas as requisições de perfis
          await Promise.all(profilePromises);
          
          const finalPermissions = Array.from(allPermissions);
          if (!environment.production) {
            console.debug(`Permissões finais carregadas (${finalPermissions.length}):`, finalPermissions);
          }
          
          this.userPermissionsSubject.next(finalPermissions);
          this.permissionsLoaded = true;
          this.loadingPromise = null;
          return;
        } else {
          if (!environment.production) {
            console.debug('Usuário não possui profileIds ou profilesIds, ou array está vazio');
          }
        }

        // Se não encontrou permissões, retornar array vazio
        this.userPermissionsSubject.next([]);
        this.permissionsLoaded = true;
        this.loadingPromise = null;
      } catch (error) {
        console.error('Erro ao carregar permissões do usuário:', error);
        this.userPermissionsSubject.next([]);
        this.permissionsLoaded = true;
        this.loadingPromise = null;
      }
    })();

    return this.loadingPromise;
  }

  /**
   * Recarrega as permissões do usuário
   */
  reloadPermissions(): void {
    this.permissionsLoaded = false;
    this.loadingPromise = null; // Limpar promise compartilhada para forçar recarregamento
    this.loadUserPermissions();
  }

  /**
   * Verifica se o usuário possui uma permissão específica
   * @param permissionKey A chave da permissão (ex: 'dashboards.view', 'users.create')
   * @returns true se o usuário possui a permissão
   */
  hasPermission(permissionKey: string): boolean {
    const permissions = this.userPermissionsSubject.value;
    
    if (!permissionKey || permissions.length === 0) {
      return false;
    }

    // Verificar permissão exata
    if (permissions.includes(permissionKey)) {
      return true;
    }

    // Verificar permissão com wildcard (ex: 'users.*' concede acesso a todas as permissões de users)
    const wildcardPermission = permissionKey.split('.')[0] + '.*';
    if (permissions.includes(wildcardPermission)) {
      return true;
    }

    // Verificar se possui permissão global '*'
    if (permissions.includes('*')) {
      return true;
    }

    return false;
  }

  /**
   * Verifica se o usuário possui pelo menos uma das permissões fornecidas
   * @param permissionKeys Array de chaves de permissão
   * @returns true se o usuário possui pelo menos uma das permissões
   */
  hasAnyPermission(permissionKeys: string[]): boolean {
    return permissionKeys.some(key => this.hasPermission(key));
  }

  /**
   * Verifica se o usuário possui todas as permissões fornecidas
   * @param permissionKeys Array de chaves de permissão
   * @returns true se o usuário possui todas as permissões
   */
  hasAllPermissions(permissionKeys: string[]): boolean {
    return permissionKeys.every(key => this.hasPermission(key));
  }

  /**
   * Retorna todas as permissões do usuário atual
   */
  getPermissions(): string[] {
    return [...this.userPermissionsSubject.value];
  }

  /**
   * Limpa as permissões (útil para logout)
   */
  clearPermissions(): void {
    this.userPermissionsSubject.next([]);
    this.permissionsLoaded = false;
    this.loadingPromise = null; // Limpar promise compartilhada
  }

  /**
   * Encontra a primeira rota que o usuário tem permissão para acessar
   * Ordem de prioridade: dashboard, labels, orders, invoices, users, settings
   * @returns Caminho da primeira rota acessível ou null se não tiver acesso a nenhuma
   */
  getFirstAccessibleRoute(): string | null {
    const routePermissions: { path: string; permission: string }[] = [
      { path: '/dashboard', permission: 'dashboards.view' },
      { path: '/label-print', permission: 'labels.view' },
      { path: '/seller-orders', permission: 'orders.view' },
      { path: '/invoices', permission: 'invoices.view' },
      { path: '/users', permission: 'users.view' },
      { path: '/settings', permission: 'settings.view' }
    ];

    for (const route of routePermissions) {
      if (this.hasPermission(route.permission)) {
        return route.path;
      }
    }

    return null;
  }
}
